/**
 * Accessible names for Judge.me review widgets (ported from the Murphy Door theme).
 *
 * Judge.me ships as app blocks (`shopify://apps/judge-me-reviews/...`) plus the core app embed,
 * and renders its markup asynchronously, well after this script runs. Each block below does an
 * initial sweep and then watches for late-inserted nodes with a MutationObserver:
 *   - carousel arrow buttons get an aria-label;
 *   - the "N reviews" all-reviews text is shortened;
 *   - star rows (preview badge, review widget summary and per-review ratings) get role="img" and
 *     aria-label="Rated X out of 5 stars", with the decorative glyphs aria-hidden. The review
 *     form's rating PICKER is a live control and is left alone;
 *   - the write-a-review modal returns focus to its opener on close and closes on Escape.
 *
 * ARIA attributes only, no visual change. Idempotent. Replaces assets/stamped-a11y-fix.js.
 * Tests: tests/judgeme-a11y-patches.test.js, tests/judgeme-review-modal-focus.test.js.
 */
(function patchJdgmArrows() {
  function labelBtn(btn) {
    if (!btn.getAttribute('aria-label')) {
      // Depends on Judge.me's inline onclick string containing "Previous"
      // e.g. onclick="window.jdgmPreviousCard(...)". If Judge.me renames
      // this function, update the string check below accordingly.
      var onclick = btn.getAttribute('onclick') || '';
      btn.setAttribute(
        'aria-label',
        onclick.indexOf('Previous') !== -1 ? 'Previous review' : 'Next review'
      );
    }
  }
  function labelArrows() {
    document.querySelectorAll('.jdgm-arrow').forEach(labelBtn);
  }
  var arrowObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) {
          return;
        }
        if (node.matches && node.matches('.jdgm-arrow')) {
          labelBtn(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('.jdgm-arrow').forEach(labelBtn);
        }
      });
    });
  });
  labelArrows();
  arrowObserver.observe(document.body, { childList: true, subtree: true });
})();

(function patchJdgmAllReviewsText() {
  function shortenText(span) {
    var count = span.getAttribute('data-number-of-reviews');
    if (!count) {
      return;
    }
    var anchor = span.querySelector('a');
    if (anchor && anchor.textContent.trim() !== count + ' reviews') {
      anchor.textContent = count + ' reviews';
    }
  }
  function patchAll() {
    document
      .querySelectorAll('.jdgm-all-reviews-text__text')
      .forEach(shortenText);
  }
  // Coalesced to one pass per frame: characterData + subtree fires on every text change sitewide.
  var textPending = false;
  var textObserver = new MutationObserver(function () {
    if (textPending) {
      return;
    }
    textPending = true;
    requestAnimationFrame(function () {
      textPending = false;
      patchAll();
    });
  });
  patchAll();
  textObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
})();

// Judge.me star ratings (WCAG 4.1.2 / SC 1.1.1): the preview badge (.jdgm-prev-badge,
// shown on product cards and PDPs) and the review-widget summary/per-review star rows
// (.jdgm-rev__rating, .jdgm-rev-widg__summary-stars, .jdgm-star-rating / [data-score])
// render as bare glyph spans with no accessible name — VoiceOver announces nothing.
// This gives the star ROW role="img" + "Rated N out of 5 stars" and hides the
// individual .jdgm-star glyphs from assistive tech, without touching the
// .jdgm-form star PICKER (a live control a reviewer clicks to choose a rating —
// labelling or hiding its glyphs would break/obscure an interactive control).
(function patchJdgmStarRatings() {
  // Marks the source value (rating|count, or score) an element was last labelled
  // from, so a re-run is a no-op unless Judge.me's own data-* attrs changed.
  var PROCESSED_ATTR = 'data-a11y-star-src';
  // Marks that WE set the current aria-label (vs. one Judge.me set itself), so a
  // later data-score change knows it is safe to relabel.
  var LABEL_OWNER_ATTR = 'data-a11y-star-label-owner';

  function formatRating(raw) {
    var n = parseFloat(raw);
    if (isNaN(n)) {
      return null;
    }
    // '5.00' -> '5', '4.80' -> '4.8', '4.83' -> '4.83'
    return n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }

  function reviewCountPhrase(count) {
    return count === 1 ? '1 review' : count + ' reviews';
  }

  function isTextReadable(el) {
    if (!el) {
      return false;
    }
    if (!el.textContent || !el.textContent.trim()) {
      return false;
    }
    if (
      el.hasAttribute('hidden') ||
      el.getAttribute('aria-hidden') === 'true'
    ) {
      return false;
    }
    var style = el.getAttribute('style') || '';
    return !/display\s*:\s*none/i.test(style);
  }

  function hideGlyphs(scope) {
    var glyphs = scope.querySelectorAll('.jdgm-star');
    for (var i = 0; i < glyphs.length; i++) {
      glyphs[i].setAttribute('aria-hidden', 'true');
    }
  }

  function inReviewForm(el) {
    return !!(el.closest && el.closest('.jdgm-form'));
  }

  function isInteractiveControl(el) {
    var tag = el.tagName;
    if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT') {
      return true;
    }
    var role = el.getAttribute('role');
    return (
      role === 'button' ||
      role === 'link' ||
      role === 'radio' ||
      role === 'option'
    );
  }

  // .jdgm-prev-badge: data-average-rating + data-number-of-reviews live on the
  // badge; the star glyphs sit in a .jdgm-prev-badge__stars child. Judge.me hides
  // badges with zero reviews, so a zero/blank count is skipped rather than
  // announced as "Rated 0".
  function patchBadges() {
    var badges = document.querySelectorAll('.jdgm-prev-badge');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      if (inReviewForm(badge)) {
        continue;
      }

      var reviewCount = parseInt(
        badge.getAttribute('data-number-of-reviews'),
        10
      );
      if (!reviewCount || reviewCount < 1) {
        continue;
      }

      var rating = formatRating(badge.getAttribute('data-average-rating'));
      if (rating === null) {
        continue;
      }

      var stars = badge.querySelector('.jdgm-prev-badge__stars');
      if (!stars) {
        continue;
      }

      var srcValue = rating + '|' + reviewCount;
      if (stars.getAttribute(PROCESSED_ATTR) === srcValue) {
        continue; // already labelled for this exact rating/count
      }

      var label = 'Rated ' + rating + ' out of 5 stars';
      var textEl = badge.querySelector('.jdgm-prev-badge__text');
      if (!isTextReadable(textEl)) {
        // Visible review-count text is missing/hidden, so fold the count into
        // the star row's own label instead of leaving it unannounced.
        label += ', ' + reviewCountPhrase(reviewCount);
      }

      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', label);
      stars.setAttribute(PROCESSED_ATTR, srcValue);
      hideGlyphs(stars);
    }
  }

  // Summary/per-review star rows. Judge.me's convention puts data-score directly
  // on the star container, e.g. <span class="jdgm-rev__rating" data-score="5">.
  function patchScoreContainers() {
    var els = document.querySelectorAll(
      '.jdgm-rev__rating, .jdgm-rev-widg__summary-stars, .jdgm-star-rating, [data-score]'
    );
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (inReviewForm(el) || isInteractiveControl(el)) {
        continue; // leave the review-form rating picker's stars alone
      }
      if (!el.closest || !el.closest('[class*="jdgm"]')) {
        continue; // bare [data-score] outside a Judge.me widget is not ours
      }
      if (el.closest('.jdgm-prev-badge')) {
        continue; // patchBadges() already owns the preview badge (and its stars child)
      }

      // Never turn a container with interactive content into role=img: its
      // children would become presentational. Live PDP evidence (2026-09-25):
      // the all_reviews_text app block puts data-score on BOTH the stars span
      // (.jdgm-all-reviews-rating) AND the count span wrapping the
      // "918 reviews" link (.jdgm-all-reviews-text__text) - only the former
      // is a rating graphic.
      if (el.querySelector('a, button, input, select, textarea, [tabindex]')) {
        // Judge.me can insert the link AFTER an earlier pass labelled this
        // element, so re-check on every pass and give back a label we set:
        // a role=img wrapper would hide the "N reviews" link from VoiceOver.
        if (el.getAttribute(LABEL_OWNER_ATTR) === 'true') {
          el.removeAttribute('role');
          el.removeAttribute('aria-label');
          el.removeAttribute(LABEL_OWNER_ATTR);
          el.removeAttribute(PROCESSED_ATTR);
        }
        continue;
      }

      var score = el.getAttribute('data-score');
      if (score === null) {
        continue;
      }

      if (el.getAttribute(PROCESSED_ATTR) === score) {
        continue; // already labelled for this exact score
      }

      // Never clobber a meaningful aria-label Judge.me set itself. We only ever
      // overwrite a label we set ourselves on a prior run (tracked by
      // LABEL_OWNER_ATTR) — if Judge.me owns the label, it stays Judge.me's for
      // the life of the element, even across a later data-score change.
      var weOwnLabel = el.getAttribute(LABEL_OWNER_ATTR) === 'true';
      if (!el.hasAttribute('aria-label') || weOwnLabel) {
        // Prefer the score Judge.me SHOWS (all_reviews_text renders
        // data-score 4.84 as "4.8") so VoiceOver matches what sighted users see.
        var shownScoreEl = el.querySelector('[class$="__score"]');
        var shown = shownScoreEl
          ? formatRating(shownScoreEl.textContent)
          : null;
        var rating = shown !== null ? shown : formatRating(score);
        if (rating !== null) {
          el.setAttribute('role', 'img');
          el.setAttribute('aria-label', 'Rated ' + rating + ' out of 5 stars');
          el.setAttribute(LABEL_OWNER_ATTR, 'true');
        }
      }

      el.setAttribute(PROCESSED_ATTR, score);
      hideGlyphs(el);
    }
  }

  function patchJdgmStars() {
    patchBadges();
    patchScoreContainers();
  }

  patchJdgmStars();
  // Coalesce bursts of mutations (PDPs mutate constantly while the
  // configurator runs) into at most one pass per animation frame.
  var starPassQueued = false;
  var starObserver = new MutationObserver(function () {
    if (starPassQueued) {
      return;
    }
    starPassQueued = true;
    requestAnimationFrame(function () {
      starPassQueued = false;
      patchJdgmStars();
    });
  });
  starObserver.observe(document.body, { childList: true, subtree: true });
})();

// Write-a-review modal (div.jdgm-write-review-modal, role=dialog): Judge.me moves focus into the
// modal on open, but on close it leaves focus on <body> instead of returning it to the button
// that opened it (WCAG 2.4.3), and Escape does not reliably close it. Found in WebKit UAT on
// 2026-10-13. This remembers the opener, returns focus to it when the modal closes, and closes
// the modal on Escape through Judge.me's own close button. Safari does not focus a button on
// click, so the opener is taken from the last pointer/keyboard activation, not activeElement.
(function patchJdgmReviewModal() {
  var MODAL = '.jdgm-write-review-modal';
  var CLOSE = '.jdgm-write-review-modal__close-btn';
  var lastActivated = null;
  var opener = null;
  var openEl = null;
  var pending = false;

  function openModal() {
    var modal = document.querySelector(MODAL);
    if (!modal || modal.getAttribute('aria-hidden') === 'true') {
      return null;
    }
    var style = window.getComputedStyle(modal);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return null;
    }
    return modal;
  }

  function remember(event) {
    var target = event.target;
    if (target && target.closest && !target.closest(MODAL)) {
      lastActivated =
        target.closest('a, button, [role="button"], [tabindex]') || target;
    }
  }
  document.addEventListener('click', remember, true);
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        remember(event);
      }
    },
    true
  );

  function sync() {
    pending = false;
    var modal = openModal();
    if (modal && !openEl) {
      opener = lastActivated;
    } else if (!modal && openEl) {
      var active = document.activeElement;
      // Focus is "lost" on <body>, on a detached node, or still inside the modal Judge.me just
      // hid with display:none (the close button keeps focus in that case).
      var focusLost =
        !active ||
        active === document.body ||
        !document.contains(active) ||
        openEl.contains(active);
      if (
        focusLost &&
        opener &&
        document.contains(opener) &&
        typeof opener.focus === 'function'
      ) {
        opener.focus();
      }
      opener = null;
    }
    openEl = modal;
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) {
      return;
    }
    var modal = openModal();
    var close = modal && modal.querySelector(CLOSE);
    if (close) {
      close.click();
    }
  });

  // Attribute changes under <body> are constant (carousels, drawers, the configurator), so bail
  // out cheaply unless a Judge.me modal exists or was open, and coalesce to one sync per frame.
  var modalObserver = new MutationObserver(function () {
    if (pending || (!openEl && !document.querySelector(MODAL))) {
      return;
    }
    pending = true;
    requestAnimationFrame(sync);
  });
  modalObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'],
  });
})();
